// 58 §5b — THE SCHEDULE AXIS, resolved once for every surface that draws it.
//
// The four SCHED_* constants in src/utils.ts were a hardcoded working week —
// Monday to Friday, 07:00 to 20:00, half-hour slots — and a firm whose Saturday
// is a working day had nowhere to say so. `FirmSettings.scheduleAxis` is that
// firm's answer; this module is the ONE place that reads it, so the six
// surfaces cannot disagree about what a week is.
//
// DEFAULTS REPRODUCE TODAY'S RENDERING EXACTLY, and that is checkable rather
// than promised: with no setting at all the resolver returns start 7, end 20,
// Monday–Friday working at width 2, Saturday and Sunday non-working at width 1
// — which is the `flex: isWknd ? 1 : 2` / `minWidth: isWknd ? 40 : 80` the two
// 14-day strips already drew by hand, and the `dw === 0 || dw === 6` the
// weekend test already answered. An absent KEY falls back the same way an
// absent SETTING does, so a partially-filled row cannot produce a half-defined
// week.
//
// WIDTH IS NOT A SECOND WAY OF SAYING "WORKING". A firm may want Saturday
// narrow and workable, or wide and closed. Nothing reads one for the other.
//
// 132 §3 — THE SENTENCE THAT FOLLOWED THAT ONE WAS WRONG IN BOTH HALVES, and
// is withdrawn here rather than left to argue with the code. It said "The
// strips read `width`; the schedule grids read `working`". Censused on
// `84c1b278`, every reader in the tree:
//
//   .working   matterListCfg.tsx:659, Dashboard.tsx:783 — the two 14-day
//              strips, for the day-label colour
//   .width     PlacesView.tsx:1386 — the availability overview's column
//              template, and NOTHING ELSE since 132 §3 stopped the strips
//              reading it (RULED: a non-working day is drawn at the same width
//              as a working one there)
//   isNonWorkingDow   PlacesView.tsx:1436/1464/1539 — its weekend tint and
//              header colour, which §3 deliberately leaves alone
//   days/slots/start  ScheduleGrid.tsx, ScheduleView.tsx
//
// The schedule grids read `days`, `slots` and `start`. They do NOT read
// `working` and never have: the grid draws every axis day and filters none.
import { SCHED_DAYS, SCHED_START, SCHED_END, SCHED_DEFAULT_WORKING } from "../utils";

export type AxisDay = {
  /** the SCHED_DAYS code, Monday-first */
  code: string;
  /** may a schedule be painted on this day, and do the schedule grids show it */
  working: boolean;
  /** column weight on the 14-day strips: 2 is a full column, 1 a half */
  width: number;
};

export type ScheduleAxis = {
  /** first hour on the vertical axis */
  start: number;
  /** last hour on the vertical axis */
  end: number;
  /** half-hour rows between start and end — derived, never stored */
  slots: number;
  days: AxisDay[];
};

type StoredDay = Partial<AxisDay> | null | undefined;
type Stored = {
  start?: number | null;
  end?: number | null;
  days?: StoredDay[] | null;
} | null | undefined;

const num = (v: unknown, fallback: number): number =>
  typeof v === "number" && Number.isFinite(v) ? v : fallback;

/**
 * The firm's axis, or today's. `firmSettings?.scheduleAxis` is the only input;
 * pass the whole settings object or the axis, both work.
 */
export function resolveScheduleAxis(
  input: Stored | { scheduleAxis?: unknown } | null | undefined,
): ScheduleAxis {
  const holder = input as { scheduleAxis?: Stored } | null | undefined;
  const raw: Stored =
    holder && typeof holder === "object" && "scheduleAxis" in holder
      ? holder.scheduleAxis
      : (input as Stored);
  const start = num(raw?.start, SCHED_START);
  const endRaw = num(raw?.end, SCHED_END);
  // A firm that stores an end at or before its start would render zero rows and
  // a blank grid; the constant is the honest fallback and it is not silent —
  // the Settings input refuses the same combination.
  const end = endRaw > start ? endRaw : SCHED_END;
  const stored = Array.isArray(raw?.days) ? raw!.days! : [];
  const days = SCHED_DAYS.map((code, i) => {
    const d = stored[i] || null;
    const working = typeof d?.working === "boolean" ? d.working : i < SCHED_DEFAULT_WORKING;
    return {
      code,
      working,
      width: num(d?.width, working ? 2 : 1),
    };
  });
  return { start, end, slots: (end - start) * 2, days };
}

/** The axis as it would be stored with nothing set — what Settings starts from. */
export const defaultScheduleAxis = (): ScheduleAxis => resolveScheduleAxis(null);

/** `Date.getDay()` → the axis day, Monday-first. */
export const axisDayOf = (axis: ScheduleAxis, dow: number): AxisDay =>
  axis.days[(dow + 6) % 7];

/** Does this firm work that weekday? Replaces the bare weekend test on surfaces
 *  that mean "closed", not "Saturday or Sunday". */
export const isNonWorkingDow = (axis: ScheduleAxis, dow: number): boolean =>
  !axisDayOf(axis, dow).working;
